import React, { useEffect, useMemo, useState } from "react";
import {
  Building2,
  CalendarDays,
  CheckCircle2,
  Clock3,
  Globe,
  Linkedin,
  Mail,
  MapPin,
  Pencil,
  Phone,
  Tag,
  UserRound,
} from "lucide-react";
import { useNavigate, useParams } from "react-router-dom";
import { BreadCrumbs } from "@/shared/components/ui/BreadCrumb/BreadCrumbs";
import CommonAvatar from "@/shared/components/ui/Avatar/CommonAvatar";
import Loader from "@/shared/components/ui/Loaders/Loader";
import SecondaryButton from "@/shared/components/ui/Button/SecondaryButton";
import { StatusChip } from "@/shared/components/ui/Status/StatusChip";
import { transformContactData } from "@/shared/utils/helperFunctions";
import { buildCampaignActivityTimeline } from "@/features/accounts/utils/campaignActivityUtils";
import { useContactDetail, useUpdateContactConsent } from "../hooks/useContacts";
import EditContactModal from "./EditContactModal";

const formatDateTime = (value) => {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
};

const displayValue = (value) => {
  if (value === false) return "No";
  if (value === true) return "Yes";
  if (Array.isArray(value)) return value.length ? value.join(", ") : "—";
  if (value == null || String(value).trim() === "" || value === "N/A") return "—";
  return String(value);
};

const toHref = (value) => {
  const text = String(value || "").trim();
  if (!text) return "";
  return /^https?:\/\//i.test(text) ? text : `https://${text}`;
};

const DetailItem = ({ label, value, href, icon: Icon }) => {
  const content = displayValue(value);
  return (
    <div className="group flex min-h-[74px] flex-col justify-center gap-1.5 rounded-xl border border-gray-100 bg-gray-50/70 px-4 py-3 transition-colors hover:border-secondary/20 hover:bg-gray-100 dark:border-gray-800/70 dark:bg-gray-800/40 dark:hover:border-secondary/40 dark:hover:bg-gray-800">
      <span className="flex items-center gap-1.5 text-[11px] font-medium uppercase tracking-[0.06em] text-gray-400 dark:text-gray-500">
        {Icon ? <Icon size={13} className="shrink-0" /> : null}
        {label}
      </span>
      {href && content !== "—" ? (
        <a
          href={href}
          target="_blank"
          rel="noreferrer"
          className="break-words text-[13px] font-semibold text-secondary hover:underline dark:text-secondary-light"
        >
          {content}
        </a>
      ) : (
        <span className="break-words text-[13px] font-semibold leading-5 text-gray-800 dark:text-gray-100">
          {content}
        </span>
      )}
    </div>
  );
};

const DetailSection = ({ title, children }) => (
  <section className="overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm dark:border-gray-800 dark:bg-gray-900">
    <div className="border-b border-gray-200 bg-gray-50/70 px-5 py-4 dark:border-gray-800 dark:bg-gray-800/30">
      <h2 className="text-sm font-semibold text-gray-800 dark:text-gray-100">{title}</h2>
    </div>
    <div className="grid grid-cols-1 gap-3 p-5 md:grid-cols-2">{children}</div>
  </section>
);

const statusVariant = (status) => {
  const value = String(status || "").toLowerCase();
  if (value === "active" || value === "completed") return "success";
  if (value === "pending") return "warning";
  if (value === "failed" || value === "cancelled") return "danger";
  return "neutral";
};

const EnrollmentCard = ({ enrollment }) => (
  <div className="rounded-xl border border-gray-200 bg-gray-50/50 p-4 shadow-sm dark:border-gray-800 dark:bg-gray-800/30">
    <div className="flex items-start justify-between gap-3">
      <div className="min-w-0">
        <p className="truncate text-[13px] font-semibold text-gray-900 dark:text-gray-100">
          {enrollment.campaign_name || "Campaign"}
        </p>
        <p className="mt-1 text-[11px] text-gray-500 dark:text-gray-400">
          Enrolled {formatDateTime(enrollment.created_at)}
        </p>
      </div>
      <StatusChip
        label={enrollment.status || "Unknown"}
        variant={statusVariant(enrollment.status)}
      />
    </div>
    <div className="mt-4 grid grid-cols-1 gap-3 text-xs sm:grid-cols-3">
      <div>
        <span className="block text-[10px] uppercase tracking-wide text-gray-400 dark:text-gray-500">Current step</span>
        <span className="mt-1 block font-medium text-gray-700 dark:text-gray-200">{enrollment.current_step_order ?? "—"}</span>
      </div>
      <div>
        <span className="block text-[10px] uppercase tracking-wide text-gray-400 dark:text-gray-500">Last activity</span>
        <span className="mt-1 block font-medium text-gray-700 dark:text-gray-200">{formatDateTime(enrollment.last_step_at)}</span>
      </div>
      <div>
        <span className="block text-[10px] uppercase tracking-wide text-gray-400 dark:text-gray-500">Next run</span>
        <span className="mt-1 block font-medium text-gray-700 dark:text-gray-200">{formatDateTime(enrollment.next_execute_at)}</span>
      </div>
    </div>
  </div>
);

const ActivityTimeline = ({ activities, contactName }) => {
  const timeline = useMemo(
    () =>
      buildCampaignActivityTimeline(activities, contactName)
        .slice()
        .sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp)),
    [activities, contactName],
  );

  if (!timeline.length) {
    return (
      <div className="flex flex-col items-center justify-center gap-2 py-10 text-center">
        <Clock3 size={20} className="text-gray-300 dark:text-gray-600" />
        <p className="text-sm text-gray-500 dark:text-gray-400">No campaign activity yet.</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col">
      {timeline.map((item, index) => (
        <div key={`${item.id}-${index}`} className="flex gap-3">
          <div className="flex w-3 shrink-0 flex-col items-center">
            <span className="mt-1.5 h-2.5 w-2.5 rounded-full bg-secondary ring-4 ring-secondary/10" />
            {index < timeline.length - 1 ? (
              <span className="mt-1 w-px flex-1 bg-gray-200 dark:bg-gray-800" />
            ) : null}
          </div>
          <div className="min-w-0 flex-1 pb-5">
            <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1">
              <p className="text-[13px] font-semibold text-gray-800 dark:text-gray-100">
                {item.title}
              </p>
              <span className="text-[10px] text-gray-400 dark:text-gray-500">
                {formatDateTime(item.timestamp)}
              </span>
            </div>
            <p className="mt-1 text-xs leading-5 text-gray-500 dark:text-gray-400">
              {item.hint || item.description}
            </p>
          </div>
        </div>
      ))}
    </div>
  );
};

const ContactDetailPage = () => {
  const navigate = useNavigate();
  const { contactId } = useParams();
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [consentSource, setConsentSource] = useState("manual_confirmation");
  const { mutate: updateConsent, isPending: isUpdatingConsent } = useUpdateContactConsent();
  const { data, isLoading, isError, error } = useContactDetail(contactId);

  const rawContact = data?.contact;
  const contact = useMemo(
    () => (rawContact ? transformContactData(rawContact) : null),
    [rawContact],
  );

  useEffect(() => {
    setConsentSource(contact?.emailConsentSource || "manual_confirmation");
  }, [contact?.emailConsentSource]);

  if (isLoading) {
    return (
      <div className="flex w-full flex-col gap-4">
        <BreadCrumbs
          items={[{ label: "Contacts", href: "/contacts" }, { label: "Contact Details" }]}
        />
        <div className="flex h-[calc(100dvh-150px)] items-center justify-center">
          <Loader text="Loading contact details..." />
        </div>
      </div>
    );
  }

  if (isError || !contact || !rawContact) {
    return (
      <div className="flex w-full flex-col gap-4">
        <BreadCrumbs
          items={[{ label: "Contacts", href: "/contacts" }, { label: "Contact Details" }]}
        />
        <div className="flex min-h-[300px] flex-col items-center justify-center gap-3 text-center">
          <p className="text-sm text-error-500">
            {error?.message || "Contact details could not be loaded."}
          </p>
          <SecondaryButton title="Back to Contacts" onClick={() => navigate("/contacts")} />
        </div>
      </div>
    );
  }

  const website = contact.companyWebsite || (rawContact.domain ? `https://${rawContact.domain}` : "");
  const enrollments = data.enrollments || [];
  const activities = data.campaignActivities || [];
  const hasEmail = Boolean(contact.email && contact.email !== "N/A");
  const emailState = !hasEmail
    ? { label: "No email", variant: "neutral" }
    : contact.emailConsentStatus === "consented"
      ? { label: "Email consented", variant: "success" }
      : contact.emailConsentStatus === "unsubscribed"
        ? { label: "Unsubscribed", variant: "warning" }
        : { label: "Consent unknown", variant: "neutral" };
  const consentStatus = contact.emailConsentStatus || "unknown";
  const readOnlyDetails = (
    <>
      <div className="grid gap-4 xl:grid-cols-2">
        <DetailSection title="Contact Information">
          <DetailItem label="Contact Owner" value={data.owner?.name || data.owner?.email} icon={UserRound} />
          <DetailItem label="Account Name" value={contact.company} icon={Building2} />
          <DetailItem label="Contact Name" value={contact.name} />
          <DetailItem label="Designation" value={contact.position} />
          <DetailItem label="Email" value={contact.email} href={contact.email ? `mailto:${contact.email}` : ""} icon={Mail} />
          <DetailItem label="Phone / WhatsApp" value={contact.whatsappNumber} href={contact.whatsappNumber && contact.whatsappNumber !== "N/A" ? `tel:${contact.whatsappNumber}` : ""} icon={Phone} />
          <DetailItem label="LinkedIn Profile" value={contact.linkedinUrl} href={toHref(contact.linkedinUrl)} icon={Linkedin} />
          <DetailItem label="Buying Role" value={contact.buyingRole} />
          <DetailItem label="Persona" value={contact.persona} />
          <DetailItem label="Lead Source" value={contact.leadSource} />
          <DetailItem label="Social Lead ID" value={contact.socialLeadId} />
          <DetailItem label="Country / Region" value={rawContact.country || rawContact.state} icon={MapPin} />
          <DetailItem label="Headline" value={contact.headline} />
          <div className="sm:col-span-2">
            <DetailItem label="Description" value={contact.description} />
          </div>
        </DetailSection>

        <DetailSection title="Company and CRM Information">
          <DetailItem label="Company Website" value={website} href={toHref(website)} icon={Globe} />
          <DetailItem label="Company LinkedIn" value={contact.companyLinkedinUrl} href={toHref(contact.companyLinkedinUrl)} icon={Linkedin} />
          <DetailItem label="Industry" value={contact.industry} icon={Tag} />
          <DetailItem label="Company Size" value={contact.companySize} />
          <DetailItem label="Tags" value={contact.tags} />
          <DetailItem label="Email Status" value={contact.emailStatus} icon={CheckCircle2} />
          <DetailItem label="Email Verification" value={rawContact.email_verification_status} icon={CheckCircle2} />
        </DetailSection>
      </div>

      <DetailSection title="System Information">
        <DetailItem label="Status" value={rawContact.status} />
        <DetailItem label="Domain" value={rawContact.domain} />
        <DetailItem label="Email Opt Out" value={contact.emailOptOut} />
        <div className="sm:col-span-2 rounded-xl border border-gray-100 bg-gray-50/70 px-4 py-3 dark:border-gray-800/70 dark:bg-gray-800/40">
          <span className="text-[11px] font-medium uppercase tracking-[0.06em] text-gray-400 dark:text-gray-500">Commercial email consent</span>
          <div className="mt-2 flex flex-wrap items-center gap-2">
            <StatusChip
              label={consentStatus === "consented" ? "Consented" : consentStatus === "unsubscribed" ? "Unsubscribed" : "Unknown"}
              variant={consentStatus === "consented" ? "success" : consentStatus === "unsubscribed" ? "warning" : "neutral"}
            />
            {consentStatus !== "unknown" && (
              <select
                aria-label="Consent source"
                value={consentSource}
                onChange={(event) => setConsentSource(event.target.value)}
                id="contact-consent-source"
                className="h-8 rounded-md border border-gray-300 bg-white px-2 text-xs text-gray-700 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-200"
              >
                <option value="website_signup_form">Website signup form</option>
                <option value="customer_requested_emails">Customer requested emails</option>
                <option value="existing_business_relationship">Existing business relationship</option>
                <option value="imported_consent_record">Imported consent record</option>
                <option value="manual_confirmation">Manual confirmation</option>
                <option value="other">Other</option>
                <option value="unsubscribe_link">Unsubscribe link</option>
              </select>
            )}
            {consentStatus !== "consented" && consentStatus !== "unsubscribed" && (
              <button
                type="button"
                disabled={isUpdatingConsent}
                onClick={() => updateConsent({
                  contactId,
                  status: "consented",
                  source: consentSource || "manual_confirmation",
                })}
                className="rounded-md bg-secondary px-3 py-1.5 text-xs font-semibold text-white disabled:opacity-50"
              >
                Mark consented
              </button>
            )}
            {consentStatus !== "unknown" && consentSource !== (contact.emailConsentSource || "") && (
              <button
                type="button"
                disabled={isUpdatingConsent}
                onClick={() => updateConsent({
                  contactId,
                  status: consentStatus,
                  source: consentSource,
                })}
                className="rounded-md border border-secondary px-3 py-1.5 text-xs font-semibold text-secondary disabled:opacity-50"
              >
                Save source
              </button>
            )}
          </div>
          <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
            {contact.emailConsentSource ? `Source: ${contact.emailConsentSource}` : "Unknown contacts cannot receive campaign email."}
          </p>
        </div>
        <DetailItem label="Created" value={formatDateTime(rawContact.created_at)} icon={CalendarDays} />
        <DetailItem label="Last Updated" value={formatDateTime(rawContact.updated_at)} icon={Clock3} />
        <DetailItem label="Lead Score" value={contact.salesScore} />
        <DetailItem label="Current Campaign" value={contact.currentlyEnrolledCampaignName} />
        <DetailItem label="Engagement Score" value={contact.engagementScore} />
        <DetailItem label="Email Verification Reason" value={rawContact.email_verification_reason} />
      </DetailSection>
    </>
  );

  return (
    <div className="flex w-full flex-col gap-4 px-4 pb-4">
      <BreadCrumbs
        items={[
          { label: "Contacts", href: "/contacts" },
          { label: contact.name || "Contact Details" },
        ]}
      />

      <header className="overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm dark:border-gray-800 dark:bg-gray-900">
        <div className="h-1 bg-gradient-to-r from-secondary to-secondary-light" />
        <div className="flex flex-col gap-5 p-5 sm:p-6 lg:flex-row lg:items-center lg:justify-between">
          <div className="flex min-w-0 items-center gap-4">
            <div className="shrink-0 rounded-full p-1 ring-1 ring-secondary/20">
              <CommonAvatar name={contact.name} size="xl" />
            </div>
            <div className="min-w-0">
              <div className="flex flex-wrap items-center gap-2">
                <h1 className="truncate text-xl font-semibold tracking-tight text-gray-900 dark:text-gray-100 sm:text-2xl">
                  {contact.name || "Unnamed contact"}
                </h1>
                <StatusChip label={rawContact.status || "Contact"} variant="neutral" />
              </div>
              <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                {displayValue(contact.position)}
              </p>
              <p className="mt-2 flex items-center gap-1.5 text-xs font-medium text-gray-500 dark:text-gray-400">
                <Building2 size={14} className="text-gray-400" />
                {displayValue(contact.company)}
                {rawContact.country || rawContact.state ? (
                  <>
                    <span className="text-gray-300 dark:text-gray-700">•</span>
                    {rawContact.country || rawContact.state}
                  </>
                ) : null}
              </p>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2 lg:justify-end">
            <StatusChip label={emailState.label} variant={emailState.variant} />
            {!isEditOpen ? (
              <SecondaryButton
                title="Edit Contact"
                icon={<Pencil size={15} className="text-secondary" />}
                onClick={() => setIsEditOpen(true)}
              />
            ) : null}
          </div>
        </div>
      </header>

      {isEditOpen ? (
        <EditContactModal
          isOpen={isEditOpen}
          onClose={() => setIsEditOpen(false)}
          contact={contact}
          inline
        />
      ) : (
        readOnlyDetails
      )}

      <div className="grid gap-4 xl:grid-cols-[1.05fr_0.95fr]">
        <section className="overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm dark:border-gray-800 dark:bg-gray-900">
          <div className="border-b border-gray-200 bg-gray-50/70 px-5 py-4 dark:border-gray-800 dark:bg-gray-800/30">
            <h2 className="text-sm font-semibold text-gray-800 dark:text-gray-100">Enrollments</h2>
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
              Campaigns currently connected to this contact.
            </p>
          </div>
          <div className="flex flex-col gap-3 p-5">
            {enrollments.length ? (
              enrollments.map((enrollment) => (
                <EnrollmentCard key={enrollment.id} enrollment={enrollment} />
              ))
            ) : (
              <p className="py-8 text-center text-sm text-gray-500 dark:text-gray-400">
                This contact is not enrolled in a campaign.
              </p>
            )}
          </div>
        </section>

        <section className="overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm dark:border-gray-800 dark:bg-gray-900">
          <div className="border-b border-gray-200 bg-gray-50/70 px-5 py-4 dark:border-gray-800 dark:bg-gray-800/30">
            <div className="flex items-center gap-2">
              <Clock3 size={15} className="text-gray-400" />
              <h2 className="text-sm font-semibold text-gray-800 dark:text-gray-100">Activity timeline</h2>
            </div>
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
              Activity from the campaigns and communication systems this contact has used.
            </p>
          </div>
          <div className="p-5">
            <ActivityTimeline activities={activities} contactName={contact.name} />
          </div>
        </section>
      </div>

    </div>
  );
};

export default ContactDetailPage;
